import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { AdminHeader, AdminPage, StateBadge } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, Pencil } from "lucide-react";

const EMPTY = { title: "", description: "", body: "", category: "", date: "", status: "Ongoing", related_pils: [], state: "draft" };
function Field({ label, children }) { return <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 block">{label}</label>{children}</div>; }

export default function InitiativesAdmin() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [pils, setPils] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/initiatives?include_unpublished=true").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCats(data)); api.get("/pils?include_unpublished=true&limit=200").then(({ data }) => setPils(data.items)); }, []);

  const edit = (it) => { setF({ ...EMPTY, ...it }); setOpen(true); };
  const save = async () => {
    if (!f.title) return toast.error("Title required");
    try {
      if (f.id) await api.put(`/initiatives/${f.id}`, f); else await api.post("/initiatives", f);
      toast.success("Saved"); setOpen(false); setF(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (it) => { if (!window.confirm("Delete initiative?")) return; await api.delete(`/initiatives/${it.id}`); toast.success("Deleted"); load(); };

  return (
    <>
      <AdminHeader title="Initiatives" subtitle={`${items.length} initiatives`} actions={<Button onClick={() => { setF(EMPTY); setOpen(true); }} className="bg-npst-navy text-white" data-testid="add-initiative-btn">Add Initiative</Button>} />
      <AdminPage>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm"><thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">State</th><th className="px-4 py-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">No initiatives yet.</td></tr>
              : items.map((it) => (
                <tr key={it.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-medium">{it.title}</td><td className="px-4 py-3 text-xs">{it.category}</td>
                  <td className="px-4 py-3"><StateBadge state={it.state} /></td>
                  <td className="px-4 py-3 text-right"><div className="flex justify-end gap-3 text-slate-400"><button onClick={() => edit(it)} className="hover:text-npst-navy"><Pencil size={15} /></button><button onClick={() => del(it)} className="hover:text-red-600"><Trash2 size={15} /></button></div></td>
                </tr>
              ))}
          </tbody></table>
        </div>
      </AdminPage>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="font-serif text-2xl">{f.id ? "Edit" : "Add"} Initiative</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Title"><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="init-title" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category"><Select value={f.category || "__n"} onValueChange={(v) => set("category", v === "__n" ? "" : v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="__n">None</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Status"><Input value={f.status} onChange={(e) => set("status", e.target.value)} /></Field>
            </div>
            <Field label="Short Description"><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
            <Field label="Body"><Textarea rows={5} value={f.body} onChange={(e) => set("body", e.target.value)} /></Field>
            <Field label="Related PIL"><Select value={f.related_pils?.[0] || "__n"} onValueChange={(v) => set("related_pils", v === "__n" ? [] : [v])}><SelectTrigger><SelectValue placeholder="None" /></SelectTrigger><SelectContent><SelectItem value="__n">None</SelectItem>{pils.map((p) => <SelectItem key={p.id} value={p.id}>{p.pil_code}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="State"><Select value={f.state} onValueChange={(v) => set("state", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem></SelectContent></Select></Field>
            <Button onClick={save} className="w-full bg-npst-navy text-white" data-testid="init-save-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
